/** Solo se abren con ratón: en móviles y tabletas los iconos siguen llevando a su página. */
const HOVER_QUERY = window.matchMedia('(hover: hover) and (pointer: fine)');
const OPEN_DELAY = 110;
const CLOSE_DELAY = 260;
/** Margen de tolerancia (px) alrededor de la zona segura icono + hueco + panel. */
const TOLERANCE = 8;
const PANELES = 'mini-carrito, mini-cuenta, mini-favoritos';

/**
 * Panel que aparece al pasar el ratón por un icono de la cabecera (mismo comportamiento que el
 * mini-carrito, assets/mini-carrito.js). El icono se indica con el atributo data-trigger.
 * Se sigue el puntero y se cierra cuando sale de la zona segura formada por el icono, el hueco
 * hasta el panel y el panel.
 */
class PanelCabecera extends HTMLElement {
  /** @type {HTMLElement | null} */
  trigger = null;
  /** @type {AbortController | null} */
  #listeners = null;
  #openTimer = 0;
  #closeTimer = 0;
  #frame = 0;
  #tracking = false;

  connectedCallback() {
    const selector = this.dataset.trigger;
    this.trigger = selector ? this.closest('header-actions')?.querySelector(selector) ?? null : null;
    if (!this.trigger || typeof this.showPopover !== 'function') return;

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.trigger.addEventListener('pointerenter', this.#onTriggerEnter, { signal });
    document.addEventListener('keydown', (event) => event.key === 'Escape' && this.hide(), { signal });
    document.addEventListener('scroll', this.#onViewportChange, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#onViewportChange, { passive: true, signal });
    this.setup?.(signal);
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#track(false);
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    cancelAnimationFrame(this.#frame);
  }

  get isOpen() {
    return this.matches(':popover-open');
  }

  show() {
    clearTimeout(this.#openTimer);
    if (this.isOpen) return;
    // Un solo panel abierto a la vez
    document.querySelectorAll(PANELES).forEach((p) => p !== this && p.hide?.());
    this.render?.();
    this.#position();
    this.showPopover();
  }

  hide() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = 0;
    this.#track(false);
    if (this.isOpen) this.hidePopover();
  }

  /** @param {PointerEvent} event */
  #onTriggerEnter = (event) => {
    if (event.pointerType !== 'mouse' || !HOVER_QUERY.matches) return;
    this.#track(true);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = 0;
    if (this.isOpen) return;
    clearTimeout(this.#openTimer);
    this.#openTimer = setTimeout(() => this.show(), OPEN_DELAY);
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerType !== 'mouse') return;
    if (this.#isInside(event.clientX, event.clientY)) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = 0;
    } else {
      this.#leave();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerOut = (event) => {
    if (!event.relatedTarget) this.#leave();
  };

  #leave() {
    clearTimeout(this.#openTimer);
    if (!this.isOpen) {
      this.#track(false);
      return;
    }
    if (!this.#closeTimer) this.#closeTimer = setTimeout(() => this.hide(), CLOSE_DELAY);
  }

  /** @param {boolean} on */
  #track(on) {
    if (on === this.#tracking) return;
    this.#tracking = on;
    if (on) {
      document.addEventListener('pointermove', this.#onPointerMove, { passive: true });
      document.addEventListener('pointerout', this.#onPointerOut);
    } else {
      document.removeEventListener('pointermove', this.#onPointerMove);
      document.removeEventListener('pointerout', this.#onPointerOut);
    }
  }

  /**
   * @param {number} x
   * @param {number} y
   */
  #isInside(x, y) {
    if (!this.trigger) return false;
    /** @param {{ left: number, right: number, top: number, bottom: number }} r */
    const within = (r) => x >= r.left - TOLERANCE && x <= r.right + TOLERANCE && y >= r.top - TOLERANCE && y <= r.bottom + TOLERANCE;
    const trigger = this.trigger.getBoundingClientRect();
    if (within(trigger)) return true;
    if (!this.isOpen) return false;
    const panel = this.getBoundingClientRect();
    return within({
      left: Math.min(panel.left, trigger.left),
      right: Math.max(panel.right, trigger.right),
      top: trigger.top,
      bottom: panel.bottom,
    });
  }

  #onViewportChange = () => {
    if (!this.isOpen) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const rect = this.trigger?.getBoundingClientRect();
      if (!rect || rect.bottom <= 0 || !HOVER_QUERY.matches) return this.hide();
      this.#position();
    });
  };

  /** Debajo de la fila de la cabecera, con el borde derecho cerca del icono. */
  #position() {
    if (!this.trigger) return;
    const trigger = this.trigger.getBoundingClientRect();
    const row = this.trigger.closest('.header__row')?.getBoundingClientRect();
    const top = Math.round(row && row.bottom > trigger.bottom ? row.bottom : trigger.bottom + 8);
    const right = Math.max(16, Math.round(document.documentElement.clientWidth - trigger.right - 40));
    this.style.setProperty('--panel-top', `${top}px`);
    this.style.setProperty('--panel-right', `${right}px`);
  }
}

/** Cuenta: el contenido lo pinta Liquid (snippets/mini-cuenta.liquid). */
class MiniCuenta extends PanelCabecera {}

/**
 * Favoritos: los guardados en el navegador (snippets/favoritos.liquid) se pintan como lista con
 * la foto principal y el nombre de cada prenda, pidiendo sus datos a /products/<handle>.js.
 */
class MiniFavoritos extends PanelCabecera {
  /** @type {Map<string, { title: string, url: string, image: string } | null>} */
  static cache = new Map();
  #version = 0;

  /** @param {AbortSignal} signal */
  setup(signal) {
    document.addEventListener('favoritos:cambio', () => this.isOpen && this.render(), { signal });
  }

  #handles() {
    try {
      return JSON.parse(localStorage.getItem('stoneshp:favoritos')) || [];
    } catch {
      return [];
    }
  }

  /** @param {string} handle */
  async #datos(handle) {
    if (MiniFavoritos.cache.has(handle)) return MiniFavoritos.cache.get(handle);
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const res = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
      // La prenda ya no existe (vendida y retirada): se quita de favoritos
      if (res.status === 404) {
        MiniFavoritos.cache.set(handle, null);
        return null;
      }
      if (!res.ok) throw new Error(String(res.status));
      const p = await res.json();
      const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=160` : '';
      const dato = { title: p.title, url: p.url, image: img };
      MiniFavoritos.cache.set(handle, dato);
      return dato;
    } catch {
      // Error de red: no se guarda en caché para reintentar la próxima vez
      return null;
    }
  }

  async render() {
    const lista = this.querySelector('[data-fav-lista]');
    const vacio = this.querySelector('[data-fav-vacio]');
    const pie = this.querySelector('[data-fav-pie]');
    const contador = this.querySelector('[data-fav-n]');
    if (!lista || !vacio) return;

    const handles = this.#handles();
    if (contador) contador.textContent = handles.length ? String(handles.length) : '';
    vacio.hidden = handles.length > 0;
    if (pie) pie.hidden = handles.length === 0;
    if (!handles.length) {
      lista.replaceChildren();
      return;
    }

    const version = ++this.#version;
    const datos = await Promise.all(handles.map((h) => this.#datos(h)));
    if (version !== this.#version) return;

    // Limpia de favoritos las prendas que ya no existen
    const retiradas = handles.filter((h, i) => datos[i] === null && MiniFavoritos.cache.get(h) === null);
    if (retiradas.length) {
      try {
        localStorage.setItem('stoneshp:favoritos', JSON.stringify(handles.filter((h) => !retiradas.includes(h))));
      } catch {}
      window.stoneshpFavoritos?.pintar?.();
    }
    const validos = datos.filter(Boolean);
    if (contador) contador.textContent = validos.length ? String(validos.length) : '';
    vacio.hidden = validos.length > 0;
    if (pie) pie.hidden = validos.length === 0;

    const filas = validos.map((d, i) => {
      const li = document.createElement('li');
      li.style.setProperty('--i', String(i));
      const a = document.createElement('a');
      a.className = 'mini-favoritos__item';
      a.href = d.url;
      const media = document.createElement('span');
      media.className = 'mini-favoritos__media';
      if (d.image) {
        const img = document.createElement('img');
        img.src = d.image;
        img.alt = '';
        img.width = 80;
        img.height = 100;
        img.loading = 'lazy';
        media.append(img);
      }
      const nombre = document.createElement('span');
      nombre.className = 'mini-favoritos__nombre';
      nombre.textContent = d.title;
      a.append(media, nombre);
      li.append(a);
      return li;
    });
    lista.replaceChildren(...filas);
  }
}

if (!customElements.get('mini-cuenta')) customElements.define('mini-cuenta', MiniCuenta);
if (!customElements.get('mini-favoritos')) customElements.define('mini-favoritos', MiniFavoritos);
